import React, { useState, useRef, useEffect } from 'react';
import { AlertCircle, ArrowRight, Briefcase, FolderPlus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection } from '../../i18n/config';
import JurisdictionSelect from './JurisdictionSelect';
import { DEFAULT_JURISDICTION, JurisdictionCode } from '../../common/jurisdictions';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface CreateCaseDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateCase: (title: string, jurisdiction: JurisdictionCode) => void;
}

const CreateCaseDialog: React.FC<CreateCaseDialogProps> = ({
  open,
  onClose,
  onCreateCase
}) => {
  const { t, i18n } = useTranslation();
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [jurisdiction, setJurisdiction] = useState<JurisdictionCode>(DEFAULT_JURISDICTION);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    if (error) setError('');
  };

  const handleCreate = () => {
    if (!title.trim()) {
      setError(t('errors.validation.caseTitleRequired'));
      return;
    }
    onCreateCase(title, jurisdiction);
    setTitle('');
    onClose();
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      onClose();
      setTitle('');
      setError('');
    }
  };

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCreate();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[450px]"
        dir={getLanguageDirection(i18n.language)}
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gold/10">
              <FolderPlus className="w-5 h-5 text-gold" />
            </div>
            <DialogTitle className="text-xl font-semibold">
              {t('common.dialogs.createCase.title')}
            </DialogTitle>
          </div>
          <DialogDescription>
            {t('common.dialogs.createCase.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="case-title">{t('common.dialogs.createCase.caseTitleLabel')}</Label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                id="case-title"
                ref={inputRef}
                value={title}
                onChange={handleTitleChange}
                placeholder={t('components.createCase.titlePlaceholder')}
                className="pl-9"
                error={!!error}
              />
            </div>
            {error && (
              <div className="flex items-center gap-1.5 text-sm text-destructive">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div>
            <JurisdictionSelect
              value={jurisdiction}
              onChange={setJurisdiction}
              label={t('common.jurisdiction.case')}
              size="small"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
          >
            {t('common.dialogs.createCase.cancel')}
          </Button>
          <div className="w-1"></div>
          <Button
            onClick={handleCreate}
            className="gap-2"
          >
            {t('common.dialogs.createCase.create')}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCaseDialog;
